import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { SITE_TYPE_LABELS } from '@/lib/clients';
import { CARGO_TYPES, SHIFT_LABELS, toLocalInput } from '@/lib/missions';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { QueryError } from '@/components/QueryError';

import type { Client, Driver, Mission, Paginated, ShiftType, Site, Vehicle } from '@logistech/ui-shared';

const NONE = '__none__';
const OTHER = '__other__';

// Aligné sur createMissionSchema du missions-service (routes/missions/index.ts)
const schema = z
  .object({
    clientId: z.string().uuid('Client requis'),
    isSubcontract: z.boolean(),
    primeContractorId: z.string(),
    originSiteId: z.string().uuid('Site de départ requis'),
    destSiteId: z.string().uuid('Site d’arrivée requis'),
    vehicleId: z.string(),
    driverId: z.string(),
    scheduledAt: z.string().min(1, 'Date requise'),
    shift: z.enum(['DAY', 'NIGHT']),
    shiftStartAt: z.string(),
    shiftEndAt: z.string(),
    targetRotations: z.string().refine(
      (v) => v === '' || (Number.isInteger(Number(v)) && Number(v) >= 1 && Number(v) <= 50),
      'Entier entre 1 et 50',
    ),
    cargoType: z.string(),
    cargoOther: z.string().max(500),
    cargoWeightTons: z.string().refine((v) => v === '' || Number(v) > 0, 'Poids invalide'),
    notes: z.string(),
  })
  .refine((d) => d.originSiteId !== d.destSiteId, {
    message: 'Le site d’arrivée doit être différent du site de départ',
    path: ['destSiteId'],
  })
  // Le missions-service exige début et fin ensemble dès qu'un poste est défini
  .refine((d) => d.shiftStartAt !== '' || d.shiftEndAt === '', { message: 'Renseigner aussi le début', path: ['shiftStartAt'] })
  .refine((d) => d.shiftEndAt !== '' || d.shiftStartAt === '', { message: 'Renseigner aussi la fin', path: ['shiftEndAt'] })
  .refine((d) => !d.shiftStartAt || !d.shiftEndAt || new Date(d.shiftEndAt) > new Date(d.shiftStartAt), {
    message: 'La fin du poste doit être postérieure au début',
    path: ['shiftEndAt'],
  })
  .refine((d) => !d.isSubcontract || z.string().uuid().safeParse(d.primeContractorId).success, {
    message: 'Donneur d’ordre requis',
    path: ['primeContractorId'],
  })
  .refine((d) => d.cargoType !== OTHER || d.cargoOther.trim() !== '', {
    message: 'Préciser la marchandise',
    path: ['cargoOther'],
  });

export type MissionFormData = z.infer<typeof schema>;

const iso = (v: string) => new Date(v).toISOString();

/**
 * create : champs vides omis. update : champs vides → null (effacement) pour les champs nullables de
 * updateMissionSchema ; cargoDesc, cargoWeightTons et notes n'y sont pas nullables : vides, ils sont omis.
 * locked (mission IN_PROGRESS) : seuls les champs encore modifiables côté API sont envoyés.
 */
export const toMissionPayload = (d: MissionFormData, mode: 'create' | 'update' | 'locked') => {
  const empty = mode === 'create' ? undefined : null;
  const hasShift = d.shiftStartAt !== '' && d.shiftEndAt !== '';
  const editable = {
    targetRotations: d.targetRotations === '' ? empty : Number(d.targetRotations),
    shiftStartAt: d.shiftStartAt ? iso(d.shiftStartAt) : empty,
    shiftEndAt: d.shiftEndAt ? iso(d.shiftEndAt) : empty,
    notes: d.notes.trim() || undefined,
  };
  if (mode === 'locked') return editable;
  const cargoDesc = d.cargoType === OTHER ? d.cargoOther.trim() : d.cargoType === NONE ? '' : d.cargoType;
  return {
    ...editable,
    clientId: d.clientId,
    primeContractorId: d.isSubcontract ? d.primeContractorId : empty,
    originSiteId: d.originSiteId,
    destSiteId: d.destSiteId,
    vehicleId: d.vehicleId && d.vehicleId !== NONE ? d.vehicleId : empty,
    driverId: d.driverId && d.driverId !== NONE ? d.driverId : empty,
    scheduledAt: iso(d.scheduledAt),
    // Sans horaires, pas de poste : l'API refuse un type de poste seul
    shift: hasShift ? d.shift : empty,
    cargoDesc: cargoDesc || undefined,
    cargoWeightTons: d.cargoWeightTons === '' ? undefined : Number(d.cargoWeightTons),
  };
};

const toDefaults = (m?: Mission): MissionFormData => {
  const cargo = m?.cargoDesc ?? '';
  const known = (CARGO_TYPES as readonly string[]).includes(cargo);
  return {
    clientId: m?.clientId ?? '',
    isSubcontract: !!m?.primeContractorId,
    primeContractorId: m?.primeContractorId ?? '',
    originSiteId: m?.originSiteId ?? '',
    destSiteId: m?.destSiteId ?? '',
    vehicleId: m?.vehicleId ?? NONE,
    driverId: m?.driverId ?? NONE,
    scheduledAt: toLocalInput(m?.scheduledAt),
    shift: m?.shift ?? 'DAY',
    shiftStartAt: toLocalInput(m?.shiftStartAt),
    shiftEndAt: toLocalInput(m?.shiftEndAt),
    targetRotations: m?.targetRotations != null ? String(m.targetRotations) : '',
    cargoType: cargo === '' ? NONE : known ? cargo : OTHER,
    cargoOther: known ? '' : cargo,
    cargoWeightTons: m?.cargoWeightTons != null ? String(Number(m.cargoWeightTons)) : '',
    notes: m?.notes ?? '',
  };
};

const siteLabel = (s: Site) => `${s.name} — ${SITE_TYPE_LABELS[s.type] ?? s.type}${s.city ? `, ${s.city}` : ''}`;

const LOCKED_CLS = 'opacity-50 cursor-not-allowed';

/** Champ grisé avec infobulle quand la mission est en cours. */
function Lockable({ locked, children, className }: { locked: boolean; children: ReactNode; className?: string }) {
  if (!locked) return <div className={className}>{children}</div>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={cn(className, LOCKED_CLS)}>{children}</div>
      </TooltipTrigger>
      <TooltipContent>Mission en cours — ce champ est verrouillé</TooltipContent>
    </Tooltip>
  );
}

const FieldError = ({ message }: { message?: string }) =>
  message ? <p className="text-xs text-danger mt-1">{message}</p> : null;

interface Props {
  /** Mission à modifier ; absente = création */
  initial?: Mission;
  submitLabel: string;
  pending: boolean;
  onSubmit: (data: MissionFormData) => void;
  onCancel: () => void;
}

export function MissionForm({ initial, submitLabel, pending, onSubmit, onCancel }: Props) {
  // Mission en cours : l'API n'accepte plus que l'objectif, les horaires du poste et les notes
  const locked = initial?.status === 'IN_PROGRESS';

  // /clients et /sites renvoient des tableaux simples ; /vehicles et /drivers sont paginés (pageSize ≤ 100)
  const clientsQuery = useQuery({ queryKey: ['clients', { showInactive: false }], queryFn: async () => (await api.get<Client[]>('/clients')).data });
  const { data: clients } = clientsQuery;
  const sitesQuery = useQuery({ queryKey: ['sites'], queryFn: async () => (await api.get<Site[]>('/sites')).data });
  const { data: sites } = sitesQuery;
  const vehiclesQuery = useQuery({ queryKey: ['vehicles', 'new-mission'], queryFn: async () => (await api.get<Paginated<Vehicle>>('/vehicles?pageSize=100&status=ACTIVE')).data });
  const { data: vehicles } = vehiclesQuery;
  const driversQuery = useQuery({ queryKey: ['drivers', 'new-mission'], queryFn: async () => (await api.get<Paginated<Driver>>('/drivers?pageSize=100&status=ACTIVE')).data });
  const { data: drivers } = driversQuery;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<MissionFormData>({ resolver: zodResolver(schema), defaultValues: toDefaults(initial) });

  const clientId = watch('clientId');
  const isSubcontract = watch('isSubcontract');
  const cargoType = watch('cargoType');
  // Sites proposés : ceux du client choisi, puis les sites internes (dépôts… sans client)
  const clientSites = (sites ?? []).filter((s) => s.clientId === clientId);
  const ownSites = (sites ?? []).filter((s) => s.clientId === null);
  const selectableSites = [...clientSites, ...ownSites];

  const onClientChange = (id: string) => {
    setValue('clientId', id, { shouldValidate: true });
    setValue('originSiteId', '');
    setValue('destSiteId', '');
    if (watch('primeContractorId') === id) setValue('primeContractorId', '');
  };

  const siteSelect = (field: 'originSiteId' | 'destSiteId', placeholder: string) => (
    <Select
      value={watch(field) || undefined}
      onValueChange={(v) => setValue(field, v, { shouldValidate: true })}
      disabled={!clientId || locked}
    >
      <SelectTrigger><SelectValue placeholder={clientId ? placeholder : 'Choisir d’abord un client'} /></SelectTrigger>
      <SelectContent>
        {selectableSites.map((s) => <SelectItem key={s.id} value={s.id}>{siteLabel(s)}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <QueryError query={[clientsQuery, sitesQuery, vehiclesQuery, driversQuery]} />
        <Card>
          <CardHeader><CardTitle>Client et trajet</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Lockable locked={locked} className="col-span-2">
              <Label>Client *</Label>
              <Select value={clientId || undefined} onValueChange={onClientChange} disabled={locked}>
                <SelectTrigger><SelectValue placeholder="Choisir un client…" /></SelectTrigger>
                <SelectContent>
                  {clients?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <FieldError message={errors.clientId?.message} />
              {clients && clients.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Aucun client actif. <Link to="/clients/new" className="underline">Créer un client</Link>
                </p>
              )}
              {clientId && selectableSites.length < 2 && (
                <p className="text-xs text-warning mt-1">
                  Ce client n’a pas assez de sites pour définir un trajet.{' '}
                  <Link to="/clients/$id" params={{ id: clientId }} className="underline">Ajouter des sites</Link>
                </p>
              )}
            </Lockable>
            <Lockable locked={locked} className="col-span-2 space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isSubcontract}
                  disabled={locked}
                  onChange={(e) => {
                    setValue('isSubcontract', e.target.checked);
                    if (!e.target.checked) setValue('primeContractorId', '', { shouldValidate: true });
                  }}
                />
                C’est une sous-traitance
              </label>
              {isSubcontract && (
                <div>
                  <Label>Donneur d’ordre final *</Label>
                  <Select
                    value={watch('primeContractorId') || undefined}
                    onValueChange={(v) => setValue('primeContractorId', v, { shouldValidate: true })}
                    disabled={locked}
                  >
                    <SelectTrigger><SelectValue placeholder="Choisir le donneur d’ordre…" /></SelectTrigger>
                    <SelectContent>
                      {clients?.filter((c) => c.id !== clientId).map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError message={errors.primeContractorId?.message} />
                </div>
              )}
            </Lockable>
            <Lockable locked={locked}>
              <Label>Site de départ *</Label>
              {siteSelect('originSiteId', 'Choisir le départ…')}
              <FieldError message={errors.originSiteId?.message} />
            </Lockable>
            <Lockable locked={locked}>
              <Label>Site d’arrivée *</Label>
              {siteSelect('destSiteId', 'Choisir l’arrivée…')}
              <FieldError message={errors.destSiteId?.message} />
            </Lockable>
            <Lockable locked={locked}>
              <Label htmlFor="scheduledAt">Date planifiée *</Label>
              <Input id="scheduledAt" type="datetime-local" readOnly={locked} {...register('scheduledAt')} />
              <FieldError message={errors.scheduledAt?.message} />
            </Lockable>
            <p className="text-xs text-muted-foreground self-end">
              La distance est calculée automatiquement si les deux sites ont des coordonnées GPS.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Shift</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Lockable locked={locked} className="col-span-2">
              <Label>Type de poste</Label>
              <div className="flex gap-6 mt-1">
                {(Object.entries(SHIFT_LABELS) as [ShiftType, string][]).map(([value, label]) => (
                  <label key={value} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="shift"
                      checked={watch('shift') === value}
                      disabled={locked}
                      onChange={() => setValue('shift', value)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </Lockable>
            <div>
              <Label htmlFor="shiftStartAt">Début du poste</Label>
              <Input id="shiftStartAt" type="datetime-local" {...register('shiftStartAt')} />
              <FieldError message={errors.shiftStartAt?.message} />
            </div>
            <div>
              <Label htmlFor="shiftEndAt">Fin du poste</Label>
              <Input id="shiftEndAt" type="datetime-local" {...register('shiftEndAt')} />
              <FieldError message={errors.shiftEndAt?.message} />
            </div>
            <div>
              <Label htmlFor="targetRotations">Nombre de voyages visés par shift</Label>
              <Input id="targetRotations" type="number" min={1} max={50} step={1} placeholder="ex: 8" {...register('targetRotations')} />
              <FieldError message={errors.targetRotations?.message} />
            </div>
            <p className="text-xs text-muted-foreground self-end">
              Optionnel. Le type de poste n’est enregistré que si le début et la fin sont renseignés.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Affectation</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Lockable locked={locked}>
              <Label>Véhicule</Label>
              <Select value={watch('vehicleId')} onValueChange={(v) => setValue('vehicleId', v)} disabled={locked}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Non affecté</SelectItem>
                  {vehicles?.data?.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.plate}{v.make ? ` — ${v.make} ${v.model ?? ''}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Lockable>
            <Lockable locked={locked}>
              <Label>Conducteur</Label>
              <Select value={watch('driverId')} onValueChange={(v) => setValue('driverId', v)} disabled={locked}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Non affecté</SelectItem>
                  {drivers?.data?.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.firstName} {d.lastName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Lockable>
            <p className="col-span-2 text-xs text-muted-foreground">
              Avec un conducteur, la mission est planifiée et apparaît dans son app chauffeur.
              Sans conducteur, elle reste en brouillon et peut être affectée plus tard.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Cargaison</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Lockable locked={locked}>
              <Label>Type de marchandise</Label>
              <Select value={cargoType} onValueChange={(v) => setValue('cargoType', v, { shouldValidate: true })} disabled={locked}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Non précisé</SelectItem>
                  {CARGO_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  <SelectItem value={OTHER}>Autre</SelectItem>
                </SelectContent>
              </Select>
              {cargoType === OTHER && (
                <>
                  <Input className="mt-2" readOnly={locked} {...register('cargoOther')} placeholder="Préciser la marchandise…" />
                  <FieldError message={errors.cargoOther?.message} />
                </>
              )}
            </Lockable>
            <Lockable locked={locked}>
              <Label htmlFor="cargoWeightTons">Poids (tonnes)</Label>
              <Input id="cargoWeightTons" inputMode="decimal" readOnly={locked} {...register('cargoWeightTons')} placeholder="30" />
              <FieldError message={errors.cargoWeightTons?.message} />
            </Lockable>
            <div className="col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                autoComplete="off"
                id="notes"
                {...register('notes')}
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onCancel}>Annuler</Button>
          <Button type="submit" disabled={pending}>{pending ? 'Enregistrement…' : submitLabel}</Button>
        </div>
      </form>
    </TooltipProvider>
  );
}
